<template>
  <Card padding="none" hover>
    <div class="p-6">
      <div class="flex items-center justify-between mb-4">
        <div class="flex items-center space-x-4">
          <OrganizationAvatar :organization="organization" size="lg" />
          <div>
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
              {{ organization.name }}
              <CheckBadgeIcon
                v-if="organization.is_domain_verified"
                class="h-5 w-5 text-blue-500 ml-1"
                title="Verified Domain"
              />
            </h3>
            <div v-if="organization.tier" class="mt-1 flex items-center gap-2">
              <span
                class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium capitalize"
                :class="tierClass"
              >
                {{ organization.tier }}
              </span>
              <span
                v-if="
                  organization.subscription_status &&
                  organization.subscription_status !== 'active'
                "
                class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                :class="subStatusClass"
              >
                {{ organization.subscription_status }}
              </span>
            </div>
          </div>
        </div>
        <div
          v-if="isActive"
          class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
        >
          Active
        </div>
      </div>

      <div class="grid grid-cols-2 gap-4 mt-6">
        <div class="text-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
          <p
            class="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-semibold"
          >
            Members
          </p>
          <p class="text-xl font-bold text-gray-900 dark:text-white">{{ memberCount }}</p>
        </div>
        <div class="text-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
          <p
            class="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-semibold"
          >
            Balance
          </p>
          <p class="text-xl font-bold text-gray-900 dark:text-white">
            ${{ (organization.credit_balance || 0).toFixed(2) }}
          </p>
        </div>
      </div>

      <div class="mt-6 flex space-x-3">
        <Button class="flex-1" @click="$emit('select', organization.id)">
          {{ isActive ? 'Go to Dashboard' : 'Switch to' }}
        </Button>
        <Button v-if="canManage" variant="outline" @click="$emit('settings', organization.id)">
          <Cog6ToothIcon class="h-5 w-5" />
        </Button>
      </div>
    </div>
  </Card>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import type { Organization } from '../types';
  import OrganizationAvatar from './OrganizationAvatar.vue';
  import { CheckBadgeIcon, Cog6ToothIcon } from '@heroicons/vue/24/outline';
  import { Card, Button } from '@asabdev/asab-ui';

  interface Props {
    organization: Organization;
    isActive?: boolean;
    canManage?: boolean;
    memberCount?: number;
  }

  const props = defineProps<Props>();

  defineEmits<{
    (e: 'select', id: string): void;
    (e: 'settings', id: string): void;
  }>();

  const tierClass = computed(() => {
    switch (props.organization.tier) {
      case 'pro':
        return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300';
      case 'enterprise':
        return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
    }
  });

  const subStatusClass = computed(() => {
    switch (props.organization.subscription_status) {
      case 'past_due':
        return 'bg-red-100 text-red-800';
      case 'suspended':
        return 'bg-amber-100 text-amber-800';
      case 'canceled':
        return 'bg-gray-200 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  });
</script>
